import { useEffect, useState } from "react";
import { Modal } from "antd";
import { Drawer } from "../../../components/drawer/drawer";
import { DrawerHeader } from "../../../components/drawer/drawer-header";
import useNotification from "../../../components/notification/notification";
import ApiRoles from "../../../api/roles";
import RoleForm from "./role-form";
import {
  CONFIRM_MODAL,
  failureMessage,
  fieldErrors,
  sensitiveIn,
  validationError,
} from "./role-edit";

const BLANK = { name: "", description: "", siteScoping: "limited", permissions: [] };

/**
 * LF360-2046 — creating a custom role (draft, story 4).
 *
 * "Start from" copies another role's permissions and site scoping into the
 * form. A role with sensitive permissions is confirmed before it is created.
 */
const RoleCreate = ({ isOpen, close, catalog, roles = [], onCreated }) => {
  const { openNotification } = useNotification();
  const [draft, setDraft] = useState(BLANK);
  const [startFrom, setStartFrom] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  // Fields are marked once a create was refused, and clear as they are fixed.
  const [tried, setTried] = useState(false);

  // Every opening starts from a blank form.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(BLANK);
    setStartFrom(null);
    setConfirming(false);
    setTried(false);
  }, [isOpen]);

  // Null until something is picked, so the placeholder shows; "" once Blank is.
  const pickStart = (id) => {
    const source = roles.find((role) => role.id === id);
    setStartFrom(source ? source.id : "");
    setDraft({
      ...draft,
      siteScoping: source ? source.siteScoping : BLANK.siteScoping,
      permissions: source ? source.permissions : BLANK.permissions,
    });
  };

  const sensitive = sensitiveIn(catalog, draft.permissions);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await ApiRoles.createRole({
        name: draft.name.trim(),
        description: draft.description.trim(),
        siteScoping: draft.siteScoping,
        permissions: draft.permissions,
        ...(startFrom ? { startFrom } : {}),
      });
      openNotification({ type: "success", message: <p>Created role "{data.role.name}"</p> });
      setConfirming(false);
      onCreated?.(data.role);
    } catch (error) {
      setConfirming(false);
      openNotification({ type: "error", message: <p>{failureMessage(error, "Unable to create the role")}</p> });
    } finally {
      setBusy(false);
    }
  };

  const askToCreate = () => {
    const problem = validationError(draft);
    if (problem) {
      setTried(true);
      openNotification({ type: "error", message: <p>{problem}</p> });
      return;
    }
    if (sensitive.length) {
      setConfirming(true);
      return;
    }
    create();
  };

  return (
    <Drawer open={isOpen} onClose={close} size={800}>
      <DrawerHeader
        title={<p className="life-fact-title">New role</p>}
        isEditing
        onCancel={close}
        onSave={askToCreate}
        saveLabel="Create"
        saveTestID="role-create-save"
        isLoading={busy}
      />
      {isOpen && (
        <div style={{ paddingBottom: 48 }}>
          <RoleForm
            catalog={catalog}
            draft={draft}
            setDraft={setDraft}
            errors={tried ? fieldErrors(draft) : {}}
            startFrom={{ roles, value: startFrom ?? undefined, onChange: pickStart }}
          />
        </div>
      )}

      <Modal
        open={confirming}
        title="Create role"
        okText="Create"
        onOk={create}
        onCancel={() => setConfirming(false)}
        confirmLoading={busy}
        destroyOnHidden
        {...CONFIRM_MODAL}
      >
        <p>Create the role "{draft.name.trim()}"?</p>
        <p className="role-confirm__sensitive">
          This role includes sensitive permissions: {sensitive.join(", ")}. Continue?
        </p>
      </Modal>
    </Drawer>
  );
};

export default RoleCreate;
